/**
 * Message store — the single ordered conversation timeline.
 *
 * Three sources feed it and must interleave correctly:
 * - chat messages (text mode, incl. the local optimistic echo)
 * - transcription segments (audio mode; segments UPDATE in place by id as STT
 *   refines, keeping their original position)
 * - component messages (RPC widgets, Block Kit — commit-time stamped so a
 *   buffered widget sorts strictly after the agent message that released it)
 *
 * Ordering: entries sort by timestamp. The store hands out strictly
 * monotonically increasing timestamps (sub-ms bumped when the clock hasn't
 * advanced), replacing the production widget's `Date.now() + Math.random()`
 * collision-avoidance hacks with a deterministic policy.
 */
import { createStore } from "./store.js";
const MONOTONIC_BUMP = 0.001;
export function createMessageStore(clock) {
    /** Committed messages: chat, components, selection bubbles */
    let entries = [];
    /** Live transcription segments keyed by segment id */
    const transcripts = new Map();
    /** RAG source URL waiting to attach to the next agent reply */
    let pendingSourceUrl = "";
    let lastTimestamp = 0;
    const build = () => {
        const merged = [...transcripts.values(), ...entries].sort((a, b) => a.timestamp - b.timestamp);
        return { messages: merged };
    };
    const handle = createStore(build);
    const store = {
        getSnapshot: handle.getSnapshot,
        subscribe: handle.subscribe,
        dispose: handle.dispose,
        nextTimestamp() {
            const now = clock.now();
            lastTimestamp = now > lastTimestamp ? now : lastTimestamp + MONOTONIC_BUMP;
            return lastTimestamp;
        },
        addChatMessage(params) {
            const sourceUrl = !params.isSelf && pendingSourceUrl ? pendingSourceUrl : undefined;
            if (sourceUrl)
                pendingSourceUrl = "";
            const msg = {
                name: params.name ?? (params.isSelf ? "You" : "Agent"),
                message: params.message,
                isSelf: params.isSelf,
                timestamp: params.timestamp ?? store.nextTimestamp(),
                ...(sourceUrl && { sourceUrl }),
            };
            entries.push(msg);
            handle.commit();
            return msg;
        },
        addComponent(component) {
            const msg = {
                name: "Agent",
                message: "",
                isSelf: false,
                timestamp: store.nextTimestamp(),
                componentType: component.component_type,
                componentData: component,
            };
            entries.push(msg);
            handle.commit();
            return msg;
        },
        addSystemLog(params) {
            entries.push({
                name: "system",
                message: params.label,
                isSelf: false,
                timestamp: store.nextTimestamp(),
                kind: "system",
                ...(params.meta && { systemMeta: params.meta }),
                systemLevel: params.level,
            });
            handle.commit();
        },
        addLocalSelection(componentId, label) {
            const prompt = entries.find((m) => m.componentData?.component_id === componentId);
            const anchor = prompt ? prompt.timestamp + MONOTONIC_BUMP / 2 : store.nextTimestamp();
            entries.push({ name: "You", isSelf: true, message: label, timestamp: anchor });
            handle.commit();
        },
        upsertTranscription(segment) {
            const existing = transcripts.get(segment.id);
            transcripts.set(segment.id, {
                name: segment.participantName ?? (segment.isLocal ? "You" : "Agent"),
                message: segment.text,
                isSelf: segment.isLocal,
                timestamp: existing?.timestamp ?? store.nextTimestamp(),
                ...(existing?.sourceUrl && { sourceUrl: existing.sourceUrl }),
            });
            // Voice-mode RAG citations (App.tsx:1094-1118): a pending send_source_urls
            // value appends to the LAST transcription when that is an agent segment
            // without a URL yet. Whether it appended or not, the pending value is
            // consumed — production clears the ref on every update that doesn't
            // append, and the append itself is once-only (index guard + !sourceUrl).
            if (pendingSourceUrl) {
                let newestId;
                let newest;
                for (const [id, msg] of transcripts) {
                    if (!newest || msg.timestamp >= newest.timestamp) {
                        newest = msg;
                        newestId = id;
                    }
                }
                if (newest && newestId !== undefined && !newest.isSelf && !newest.sourceUrl) {
                    transcripts.set(newestId, { ...newest, sourceUrl: pendingSourceUrl });
                }
                pendingSourceUrl = "";
            }
            handle.commit();
        },
        setPendingSourceUrl(url) {
            pendingSourceUrl = url;
        },
        clearPendingSourceUrl() {
            pendingSourceUrl = "";
        },
        reset() {
            entries = [];
            transcripts.clear();
            pendingSourceUrl = "";
            handle.commit();
        },
    };
    return store;
}
